import { useState } from "react";
import {
  User,
  Mail,
  Calendar,
  Search,
  ChevronDown,
  MoreVertical,
} from "lucide-react";
import {
  useGetAllUsersQuery,
  useUpdateUserRoleMutation,
  useDeleteUserMutation,
  useGetUserByIdQuery,
} from "../../../redux/slices/authSlice";
import CustomerDetailsModal from "../../../components/dashboard/admin/CustomerDetailsModal";
import LoadingSkeleton from "../../../components/preloader/LoadingSkeleton";
import ErrorMessage from "../../../components/common/ErrorMessage";
import SEO from "../../../components/SEO";
import ConfirmModal from "../../../components/common/ConfirmModal";

const Customers = () => {
  const { data: users = [], isLoading, isError } = useGetAllUsersQuery();
  const [updateUserRole] = useUpdateUserRoleMutation();
  const [deleteUser] = useDeleteUserMutation();
  const [searchTerm, setSearchTerm] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Confirm delete modal states
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [userIdToDelete, setUserIdToDelete] = useState(null);

  // Fetch detailed user data when a user is selected
  const { data: selectedUser } = useGetUserByIdQuery(selectedUserId, {
    skip: !selectedUserId,
  });

  const toggleDropdown = (id) => {
    setDropdownOpen(dropdownOpen === id ? null : id);
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await updateUserRole({ id: userId, role: newRole }).unwrap();
      setDropdownOpen(null);
    } catch (error) {
      console.error("Failed to update role:", error);
    }
  };

  // Handle delete user with custom confirmation modal trigger
  const handleDeleteUser = (userId) => {
    setUserIdToDelete(userId);
    setIsConfirmOpen(true);
    setDropdownOpen(null);
  };

  const handleConfirmDelete = async () => {
    if (userIdToDelete) {
      try {
        await deleteUser(userIdToDelete).unwrap();
      } catch (error) {
        console.error("Failed to delete user:", error);
      }
    }
    setIsConfirmOpen(false);
    setUserIdToDelete(null);
  };

  const handleViewDetails = (userId) => {
    setSelectedUserId(userId);
    setIsModalOpen(true);
    setDropdownOpen(null);
  };

  const filteredUsers = users.filter(
    (user) =>
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading)
    return (
      <div className="space-y-4">
        <LoadingSkeleton type={"list"} count={5} />
      </div>
    );
  if (isError) return <ErrorMessage error={"Error loading users."} />;

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <SEO
        title="Customers"
        description="AI-Powered Social-Ecommerce Platform is a comprehensive system integrating eCommerce, social networking, and MLM for book sales, community engagement, and earning opportunities."
        name="AI-Powered Social-Ecommerce"
        type="description"
      />
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        <h2 className="text-xl font-bold text-gray-800">Customers</h2>
        <div className="relative mt-4 md:mt-0">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search customers..."
            className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full md:w-64"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Customer
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Email
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Member Since
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Role
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredUsers.reverse().map((user) => (
              <tr key={user._id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="bg-purple-100 h-8 w-8 rounded-full flex items-center justify-center text-purple-600 font-bold mr-3">
                      {user.name.split(" ").map((i) => i.charAt(0))}
                    </div>
                    <div className="text-sm font-medium text-gray-900">
                      {user.name}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <div className="flex items-center">
                    <Mail className="h-4 w-4 text-gray-400 mr-1" />
                    {user.email}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <div className="flex items-center">
                    <Calendar className="h-4 w-4 text-gray-400 mr-1" />
                    {new Date(user.createdAt).toLocaleDateString()}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`px-2 py-1 text-xs rounded-full ${
                      user.role === "admin"
                        ? "bg-blue-100 text-blue-800"
                        : "bg-green-100 text-green-800"
                    }`}
                  >
                    {user.role}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium relative">
                  <div className="flex items-center space-x-2">
                    <button
                      className="text-purple-600 hover:text-purple-900 mr-2"
                      onClick={() => handleViewDetails(user._id)}
                    >
                      View
                    </button>
                    <button
                      className="text-purple-600 hover:text-purple-900"
                      onClick={() => toggleDropdown(user._id)}
                    >
                      <MoreVertical className="h-5 w-5" />
                    </button>

                    {dropdownOpen === user._id && (
                      <div className="absolute right-0 mt-8 w-48 bg-white rounded-md shadow-lg z-10">
                        <div className="py-1">
                          {user.role === "admin" ? (
                            <button
                              onClick={() => handleRoleChange(user._id, "user")}
                              className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            >
                              Make Regular User
                            </button>
                          ) : (
                            <button
                              onClick={() =>
                                handleRoleChange(user._id, "admin")
                              }
                              className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            >
                              Make Admin
                            </button>
                          )}

                          <button
                            onClick={() => handleDeleteUser(user._id)}
                            className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                          >
                            Delete User
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <CustomerDetailsModal
          customer={selectedUser}
          onClose={() => setIsModalOpen(false)}
        />
      )}

      {/* Custom Confirm Modal */}
      <ConfirmModal
        isOpen={isConfirmOpen}
        title="Delete User"
        message="Are you sure you want to delete this user? This action will permanently remove their account."
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setIsConfirmOpen(false);
          setUserIdToDelete(null);
        }}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  );
};

export default Customers;
